import { LoginCredentialsTypeEnum } from '@codefluegel/wowis-rest-typescript-client';
import { Alert, Box, Snackbar, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useAuthApi } from '../../ApiClient';
import useMedia from '../../hooks/useMedia';
import { useStore } from '../../store/context';
import ThemedButton from '../button/ThemedButton';

interface PasswordForm {
  password: string;
}

const PasswordModal = observer(
  ({
    open,
    onClose,
    onPasswordPassed,
  }: {
    open: boolean;
    onClose: () => void;
    onPasswordPassed: () => void;
  }): JSX.Element => {
    const { control, handleSubmit } = useForm<PasswordForm>();
    const { welcomeStore } = useStore();
    const authApi = useAuthApi();

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [pending, setPending] = useState(false);
    const [password, setPassword] = useState('');
    const [snackbarMessage, setSnackbarMessage] = useState('Error');
    const isMobile = useMedia('md');

    const handleClose = () => {
      onClose();
    };

    const saveEntry = async () => {
      setSnackbarMessage('Error');
      setSnackbarOpen(false);
      setPending(true);
      try {
        const customerId = welcomeStore.getWelcomeData()?.userData?.customerId;
        const { data: authResponse } = await authApi.getLoginTokens({
          username: `win${customerId}`,
          password,
          grant_type: 'password',
          type: LoginCredentialsTypeEnum.Customer,
        });
        if (authResponse.success) {
          onPasswordPassed();
          handleClose();
        } else {
          throw new Error('Login failed');
        }
      } catch (error) {
        if (error instanceof Error && typeof error.message === 'string') {
          if (error.message.includes('403')) {
            setSnackbarMessage('Passwort falsch');
          } else {
            setSnackbarMessage(error.message);
          }
        }
        setSnackbarOpen(true);
        setPending(false);
      } finally {
        setPending(false);
      }
    };

    return (
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle color="secondary">Kontaktdaten speichern</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              paddingX: isMobile ? 0 : 10,
            }}
          >
            <Typography>
              Um Ihre Daten zu ändern, bestätigen Sie bitte Ihre Identität,
              indem Sie Ihr Passwort erneut eingeben.
            </Typography>
            <Controller
              name="password"
              control={control}
              rules={{ required: 'Pflichtfeld!' }}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <TextField
                  sx={{
                    marginTop: 2,
                    marginBottom: 2,
                    width: 'auto',
                    input: { color: '#58585A' },
                  }}
                  id="passwort"
                  label="Passwort eingeben "
                  type="password"
                  variant="standard"
                  value={value}
                  error={!!error}
                  onChange={e => {
                    onChange(e.target.value);
                    setPassword(e.target.value);
                  }}
                  helperText={error ? error.message : null}
                  required
                />
              )}
            />

            <DialogActions
              sx={{
                justifyContent: 'space-around',
                marginTop: 4,
                marginBottom: 2,
              }}
            >
              <Button
                sx={{ width: 180 }}
                disabled={pending}
                onClick={handleClose}
              >
                Abbrechen
              </Button>
              <Box width={180}>
                <ThemedButton
                  label="Speichern"
                  onClick={handleSubmit(saveEntry)}
                />
              </Box>
            </DialogActions>
          </Box>
        </DialogContent>
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={() => setSnackbarOpen(false)}
        >
          <Alert severity="error" variant="filled" sx={{ width: '100%' }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Dialog>
    );
  },
);

export default PasswordModal;
